import React from 'react';
import { ShieldCheckIcon, CheckIcon } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';

const TIMELINE_LABELS = {
  '6_12_months': '6–12 Months',
  '1_2_years':   '1–2 Years',
  '3_plus_years': '3+ Years',
};

const INDUSTRY_LABELS = {
  trades_construction:   'Trades & Construction',
  manufacturing:         'Manufacturing',
  professional_services: 'Professional Services',
  retail:                'Retail',
  technology:            'Technology',
  hvac:                  'HVAC',
  restaurant:            'Restaurant',
  trades:                'Trades',
  health:                'Health',
  auto:                  'Auto',
  saas:                  'SaaS',
  other:                 'Other',
};

const formatCurrency = (value) => {
  if (value == null) return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(num);
};

export default function DealRoomListingPage({
  listing,
  isOwner = false,
  loading = false,
  persona = 'BUYER',
  userName = '',
  userInitials = '',
  planProgress = { completed: 0, total: 5, percentage: 0 },
  actionPlanBadge = 0,
  onEdit,
  onPublish,
  onArchive,
  onRelist,
  onRequestIntro,
  onRenew,
  onMarkUnderLoi,
  onMarkUnderContract,
  onMarkClosed,
  onRollback,
}) {
  const layoutProps = { persona, userName, userInitials, planProgress, actionPlanBadge };

  if (loading) {
    return (
      <AppLayout {...layoutProps}>
        <div className="max-w-[1200px] mx-auto px-6 py-12">
          <p className="text-sm text-muted-foreground">Loading listing…</p>
        </div>
      </AppLayout>
    );
  }

  if (!listing) {
    return (
      <AppLayout {...layoutProps}>
        <div className="max-w-[1200px] mx-auto px-6 py-12">
          <p className="text-sm text-muted-foreground">Listing not found.</p>
        </div>
      </AppLayout>
    );
  }

  const photos = listing.photo_urls || [];
  const isDraft = listing.deal_state === 'new_deal';
  const isPublished = listing.deal_state === 'published';
  const isArchived = listing.deal_state === 'archived';
  const isUnderLoi = listing.deal_state === 'under_loi';
  const isUnderContract = listing.deal_state === 'under_contract';
  const isClosed = listing.deal_state === 'closed_sold' || listing.deal_state === 'closed_withdrawn';
  const isOffMarket = isUnderLoi || isUnderContract || isClosed;
  const stateLabel = isDraft
    ? 'Draft'
    : isPublished
      ? 'Live'
      : isUnderLoi
        ? 'Under LOI'
        : isUnderContract
          ? 'Under Contract'
          : listing.deal_state === 'closed_sold'
            ? 'Closed · Sold'
            : listing.deal_state === 'closed_withdrawn'
              ? 'Closed · Withdrawn'
              : 'Archived';

  const industry = INDUSTRY_LABELS[listing.industry] || listing.industry || '—';
  const timeline = TIMELINE_LABELS[listing.timeline] || listing.timeline || '—';
  const structure = (listing.deal_preferences || []).join(' + ') || '—';
  const stats = [
    { label: 'Views this week',  value: listing.views_this_week  ?? '—' },
    { label: 'Buyer inquiries',  value: listing.inquiries_count  ?? '—' },
    { label: 'NDA requests',     value: listing.nda_requests_count ?? '—' },
  ];

  return (
    <AppLayout {...layoutProps}>
      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-6">
          <span
            className={`shrink-0 font-mono text-[11px] uppercase tracking-wide ${
              isPublished ? 'text-[hsl(var(--succeedo-orange))]' : 'text-muted-foreground'
            }`}
          >
            {isOwner ? `Your listing · ${stateLabel}` : 'Confidential listing'}
          </span>
        </div>

        {/* TTL banner — owner-only, shown on published listings approaching expiry */}
        {isOwner && isPublished && listing.expiring_soon && !listing.expired && (
          <div className="bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/30 rounded-[16px] p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] mb-1">
                LISTING EXPIRES IN {listing.days_until_expiry === 0 ? 'TODAY' : `${listing.days_until_expiry} DAY${listing.days_until_expiry === 1 ? '' : 'S'}`}
              </div>
              <p className="text-[13px] text-foreground">
                Renew to keep your listing live for another 90 days. Buyer inquiries continue uninterrupted.
              </p>
            </div>
            <button
              type="button"
              onClick={onRenew}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-[9px] text-sm hover:opacity-90 transition-opacity"
            >
              Renew listing
            </button>
          </div>
        )}

        {/* D15 — Lifecycle stepper for owner on published+ listings */}
        {isOwner && (isPublished || isOffMarket) && (
          <LifecycleStepper
            dealState={listing.deal_state}
            counterpartyName={listing.counterparty_name}
            expectedCloseDate={listing.expected_close_date}
            onMarkUnderLoi={onMarkUnderLoi}
            onMarkUnderContract={onMarkUnderContract}
            onMarkClosed={onMarkClosed}
            onRollback={onRollback}
          />
        )}

        {/* Buyer-facing closed/off-market notice */}
        {!isOwner && isOffMarket && (
          <div className="bg-muted/40 border-l-4 border-l-[hsl(var(--succeedo-orange))] border border-border rounded-[16px] p-5 mb-6">
            <div className="font-mono text-[11px] uppercase tracking-wide text-[hsl(var(--succeedo-orange))] mb-2">
              {isUnderLoi && 'LISTING UNDER LOI'}
              {isUnderContract && 'LISTING UNDER CONTRACT'}
              {listing.deal_state === 'closed_sold' && 'LISTING CLOSED · SOLD'}
              {listing.deal_state === 'closed_withdrawn' && 'LISTING CLOSED · WITHDRAWN'}
            </div>
            <p className="text-[13px] text-foreground">
              {isClosed
                ? 'This listing is no longer accepting new inquiries — the seller has closed the listing.'
                : 'This listing is no longer accepting new inquiries — the seller has accepted an LOI from another buyer. If the deal falls through, it may return to the marketplace.'}
            </p>
          </div>
        )}

        {/* Expired banner */}
        {isOwner && listing.expired && (
          <div className="bg-muted/40 border border-border rounded-[16px] p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                LISTING EXPIRED
              </div>
              <p className="text-[13px] text-foreground">
                This listing has been archived because it expired. Re-list to bring it back to the marketplace.
              </p>
            </div>
            <button
              type="button"
              onClick={onRelist}
              className="bg-foreground text-background rounded-full px-5 py-[9px] text-sm hover:bg-foreground/90 transition-colors"
            >
              Re-list this business
            </button>
          </div>
        )}

        {/* Owner stats / actions bar */}
        {isOwner && (
          <div className="bg-card border border-border rounded-[16px] p-4 flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-8">
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-mono text-[11px] uppercase text-muted-foreground/40 mb-1">{s.label}</div>
                  <div className="text-[22px] font-semibold text-foreground">{s.value}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {isDraft && (
                <button
                  type="button"
                  onClick={onPublish}
                  className="bg-foreground text-background rounded-full px-5 py-[9px] text-sm hover:bg-foreground/90 transition-colors"
                >
                  Publish
                </button>
              )}
              {isPublished && (
                <button
                  type="button"
                  onClick={onArchive}
                  className="border border-border text-foreground rounded-full px-5 py-[9px] text-sm hover:bg-accent transition-colors"
                >
                  Archive
                </button>
              )}
              {isArchived && (
                <button
                  type="button"
                  onClick={onRelist}
                  className="bg-foreground text-background rounded-full px-5 py-[9px] text-sm hover:bg-foreground/90 transition-colors"
                >
                  Re-list this business
                </button>
              )}
              {!isArchived && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="border border-border text-foreground rounded-full px-5 py-[9px] text-sm hover:bg-accent transition-colors"
                >
                  Edit Listing
                </button>
              )}
            </div>
          </div>
        )}

        {photos.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            {photos.slice(0, 6).map((url, i) => (
              <img
                key={url}
                src={url}
                alt={`Photo ${i + 1}`}
                className="rounded-[12px] aspect-[4/3] object-cover bg-muted"
              />
            ))}
          </div>
        )}

        {/* Listing overview card */}
        <div className="bg-card border border-border rounded-[16px] p-6 mb-6">
          <div className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-6">
            LISTING OVERVIEW
          </div>
          <dl className="space-y-3">
            <Row label="Industry"            value={industry} />
            <Row label="Location"            value={listing.province || '—'} />
            <Row label="Years in operation"  value={listing.years_in_operation ?? '—'} />
            <Row label="Revenue"             value={formatCurrency(listing.revenue)} />
            <Row label="EBITDA"              value={formatCurrency(listing.adjusted_ebitda)} />
            <Row label="Employees"           value={listing.employees ?? '—'} />
            <Row label="Asking Price"        value={formatCurrency(listing.asking_price)} bold />
            <Row label="Deal Preferences"    value={structure} />
            <Row label="Timeline"            value={timeline} />
            <Row label="Owner"               value={listing.masked_owner_name || '—'} />
          </dl>

          {(listing.business_story || listing.key_strengths) && (
            <div className="mt-8 space-y-6">
              {listing.business_story && (
                <div>
                  <h3 className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-3">
                    Business Story
                  </h3>
                  <p className="text-[14px] text-foreground whitespace-pre-line">{listing.business_story}</p>
                </div>
              )}
              {listing.key_strengths && (
                <div>
                  <h3 className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide mb-3">
                    Key Strengths & Opportunities
                  </h3>
                  <p className="text-[14px] text-foreground whitespace-pre-line">{listing.key_strengths}</p>
                </div>
              )}
            </div>
          )}

          {listing.confidential && (
            <div className="mt-6 pt-6 border-t border-border flex items-center gap-2">
              <ShieldCheckIcon className="w-3.5 h-3.5 text-[hsl(var(--chart-2))]" />
              <span className="font-mono text-[10px] uppercase text-[hsl(var(--chart-2))]/80 tracking-wide">
                CONFIDENTIAL UNTIL NDA SIGNED
              </span>
            </div>
          )}
        </div>

        {/* Buyer CTA */}
        {!isOwner && isPublished && (
          <div className="bg-card border border-l-2 border-l-[hsl(var(--succeedo-orange))] border-border rounded-[16px] p-5">
            <div className="inline-block bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))]/20 text-[hsl(var(--succeedo-orange))] font-mono text-[11px] uppercase rounded-full px-3 py-1 mb-3">
              CONFIDENTIAL LISTING
            </div>
            <h3 className="text-[16px] font-semibold text-foreground mb-2">Connect with a Business Broker</h3>
            <p className="text-[14px] text-muted-foreground mb-4">
              A broker can quietly verify the seller and broker an introduction once you complete an NDA.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onRequestIntro}
                className="bg-foreground text-background rounded-full px-5 py-[9px] text-sm hover:bg-foreground/90 transition-colors"
              >
                Request Introduction
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Row({ label, value, bold = false }) {
  return (
    <div className="flex justify-between items-center">
      <dt className="text-[13px] text-muted-foreground/60">{label}</dt>
      <dd className={`text-[14px] text-foreground ${bold ? 'font-semibold' : ''}`}>{value}</dd>
    </div>
  );
}

// D15 lifecycle stepper — circle-and-connector visual ported from
// Polymet deal-room-listing.tsx. Each circle is colored by its
// "active state" identity (Published=orange, Under LOI=amber,
// Under Contract=blue, Closed=muted). Past steps render as filled
// muted circles with a checkmark; the current step renders in its
// active color with a checkmark; future steps render as muted
// circles containing their step number. Connector lines fill in
// when their downstream step has been reached.
function LifecycleStepper({
  dealState,
  counterpartyName,
  expectedCloseDate,
  onMarkUnderLoi,
  onMarkUnderContract,
  onMarkClosed,
  onRollback,
}) {
  const isPublished     = dealState === 'published';
  const isUnderLoi      = dealState === 'under_loi';
  const isUnderContract = dealState === 'under_contract';
  const isTerminal      = dealState === 'closed_sold' || dealState === 'closed_withdrawn';

  // For each step: has the listing reached it? (Past or current.)
  // Published is reached for any state >= published, which is always
  // true once this component renders (we only mount it for sellers
  // on published+ states).
  const reachedPublished     = true;
  const reachedUnderLoi      = isUnderLoi || isUnderContract || isTerminal;
  const reachedUnderContract = isUnderContract || isTerminal;
  const reachedClosed        = isTerminal;

  const promptOptionalDetails = (label) => {
    if (typeof window === 'undefined' || !window.prompt) return { counterpartyName: null, expectedCloseDate: null };
    const cp = window.prompt(`${label} — Buyer name (optional, leave blank to skip):`, counterpartyName || '') || null;
    const cd = window.prompt(`${label} — Expected close date YYYY-MM-DD (optional):`, expectedCloseDate || '') || null;
    return { counterpartyName: cp || null, expectedCloseDate: cd || null };
  };

  return (
    <div className="bg-card border border-border rounded-[16px] p-6 mb-6">
      <div className="flex items-center gap-2 mb-6">
        <span className="font-mono text-[11px] uppercase text-muted-foreground tracking-wide">
          LISTING LIFECYCLE
        </span>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-6">
        <StepperNode
          label="Published"
          number={1}
          isCurrent={isPublished}
          isReached={reachedPublished}
          activeClasses="bg-[hsl(var(--succeedo-orange))] text-white"
        />
        <Connector reached={reachedUnderLoi} />
        <StepperNode
          label="Under LOI"
          number={2}
          isCurrent={isUnderLoi}
          isReached={reachedUnderLoi}
          activeClasses="bg-amber-600 text-white"
        />
        <Connector reached={reachedUnderContract} />
        <StepperNode
          label="Under Contract"
          number={3}
          isCurrent={isUnderContract}
          isReached={reachedUnderContract}
          activeClasses="bg-blue-600 text-white"
        />
        <Connector reached={reachedClosed} />
        <StepperNode
          label={
            dealState === 'closed_sold' ? 'Closed · Sold' :
            dealState === 'closed_withdrawn' ? 'Closed · Withdrawn' :
            'Closed'
          }
          number={4}
          isCurrent={isTerminal}
          isReached={reachedClosed}
          activeClasses="bg-muted-foreground text-background"
        />
      </div>

      {/* Available transitions */}
      {!isTerminal && (
        <div className="flex items-center gap-2 flex-wrap">
          {isPublished && (
            <button
              type="button"
              onClick={() => {
                const { counterpartyName: cp, expectedCloseDate: cd } = promptOptionalDetails('Mark as Under LOI');
                onMarkUnderLoi?.({ counterpartyName: cp, expectedCloseDate: cd });
              }}
              className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-[8px] text-[13px] hover:opacity-90 transition-opacity"
            >
              Mark as Under LOI
            </button>
          )}
          {isUnderLoi && (
            <>
              <button
                type="button"
                onClick={() => {
                  const { counterpartyName: cp, expectedCloseDate: cd } = promptOptionalDetails('Mark as Under Contract');
                  onMarkUnderContract?.({ counterpartyName: cp, expectedCloseDate: cd });
                }}
                className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-[8px] text-[13px] hover:opacity-90 transition-opacity"
              >
                Mark as Under Contract
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Roll back to Published — paused inquiries resume. Continue?')) onRollback?.();
                }}
                className="border border-border text-foreground rounded-full px-4 py-[8px] text-[13px] hover:bg-accent transition-colors"
              >
                Roll Back to Published
              </button>
            </>
          )}
          {isUnderContract && (
            <>
              <button
                type="button"
                onClick={() => {
                  const outcome = window.prompt('Outcome — type "sold" or "withdrawn":', 'sold');
                  if (outcome) onMarkClosed?.({ outcome: outcome.toLowerCase().trim() === 'withdrawn' ? 'withdrawn' : 'sold' });
                }}
                className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-4 py-[8px] text-[13px] hover:opacity-90 transition-opacity"
              >
                Mark as Closed
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Roll back to Published — paused inquiries resume. Continue?')) onRollback?.();
                }}
                className="border border-border text-foreground rounded-full px-4 py-[8px] text-[13px] hover:bg-accent transition-colors"
              >
                Roll Back to Published
              </button>
            </>
          )}
        </div>
      )}

      {(counterpartyName || expectedCloseDate) && (
        <div className="mt-5 pt-4 border-t border-border text-[12px] text-muted-foreground space-y-1">
          {counterpartyName && <div>Buyer: <span className="text-foreground">{counterpartyName}</span></div>}
          {expectedCloseDate && <div>Expected close: <span className="text-foreground">{expectedCloseDate}</span></div>}
        </div>
      )}
    </div>
  );
}

function StepperNode({ label, number, isCurrent, isReached, activeClasses }) {
  // Three visual states:
  //   isCurrent  → filled in the step's active color, ✓ inside
  //   isReached  → filled muted (past), ✓ inside
  //   else       → muted bg, step number inside
  const circleClasses = isCurrent
    ? activeClasses
    : 'bg-muted text-muted-foreground';
  const showCheck = isReached;

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${circleClasses}`}>
        {showCheck ? <CheckIcon className="w-5 h-5" /> : <span className="text-[12px] font-medium">{number}</span>}
      </div>
      <span className="text-[11px] text-muted-foreground text-center leading-tight">{label}</span>
    </div>
  );
}

function Connector({ reached }) {
  return (
    <div className={`h-0.5 flex-1 mx-2 ${reached ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-border'}`} />
  );
}
